// Components/Services/OnlyServicesSection.jsx

import React from "react";
import { Link } from "react-router-dom";
import { FiHome, FiRefreshCw, FiLayers, FiTool } from "react-icons/fi";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const gridStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
};

const services = [
  {
    title: "ADUs & New Custom Homes",
    image: "/Images/ADUs.png",
    icon: <FiHome className="text-[#212529] w-6 h-6" />,
    link: "/services/custom-homes",
    points: [
      "Accessory Dwelling Units (ADU)",
      "Junior ADUs (JADU)",
      "Garage Conversions",
      "New Custom Homes",
    ],
  },
  {
    title: "Full Home Remodels",
    image: "/Images/full-house-remodel.png",
    icon: <FiRefreshCw className="text-[#212529] w-6 h-6" />,
    link: "/services/full-home-remodels",
    points: [
      "Full Home Remodel",
      "Bathroom Remodel",
      "Home Addition",
      "Kitchen Remodel",
    ],
  },
  {
    title: "HOA & Commercial Projects",
    image: "/Images/HOA/HOA 1.jpeg",
    icon: <FiLayers className="text-[#212529] w-6 h-6" />,
    link: "/services/hoa-commercial",
    points: [
      "HOA Building Rehabilitation",
      "Commercial Tenant Improvements",
      "Common Area Reconstruction",
      "Building Envelope & Waterproofing",
    ],
  },
  {
    title: "Specialty: Takeover Projects",
    image: "/Images/takeoverprojects.png",
    icon: <FiTool className="text-[#212529] w-6 h-6" />,
    link: "/services/takeover-projects",
    points: [
      "Failed Contractor Recovery",
      "Stalled Build Completion",
      "Code Violation Correction",
      "Construction Defect Remediation",
    ],
  },
];

const ServiceCard = ({ service }) => (
  <motion.div variants={fadeUp} className="relative flex w-full">
    <div className="bg-[#212529] rounded-xl shadow-lg overflow-hidden flex flex-col h-full w-full transition-all duration-500 hover:scale-105">

      {service.badge && (
        <div className="absolute top-3 right-3 z-30 bg-white text-[#212529] text-xs font-bold px-3 py-1 rounded-full shadow">
          {service.badge}
        </div>
      )}

      <div className="h-44 w-full overflow-hidden flex-shrink-0">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
      </div>

      <div className="p-5 pt-10 text-center flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-white min-h-[56px] flex items-center justify-center">
          {service.title}
        </h3>

        {/* Points */}
        <ul className="space-y-1.5 mt-3 mb-4 flex-1 flex flex-col items-start w-fit mx-auto">
          {service.points.map((point, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#dee2e6]">
              <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
              {point}
            </li>
          ))}
        </ul>

        <div className="mt-1 pt-1">
          <Link
            to={service.link}
            className="inline-block bg-[#f8f9fa] text-[#212529] text-sm font-medium px-4 py-2 rounded-full hover:bg-[#e9ecef] transition"
          >
            View Service
          </Link>
        </div>
      </div>
    </div>

    <div className="absolute top-[10rem] left-1/2 transform -translate-x-1/2 bg-[#f8f9fa] rounded-full p-3 shadow-lg z-20">
      {service.icon}
    </div>
  </motion.div>
);

const OnlyServicesSection = () => {
  return (
    <section className="bg-[#f8f9fa] py-12">
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >

        <motion.div className="max-w-7xl mx-auto px-4 text-center" variants={staggerContainer}>
          <motion.h2
            variants={fadeUp}
            className="text-3xl md:text-4xl font-bold text-[#212529]"
          >
            Our Services
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="mt-4 text-[#6c757d] max-w-2xl mx-auto"
          >
            From custom homes and ADUs to HOA reconstruction and takeover
            projects — Ashlar handles complex, high-value builds from rough to
            perfect.
          </motion.p>
        </motion.div>

        <motion.div className="mt-10 max-w-[1400px] mx-auto px-4" variants={gridStagger}>
          <div className="flex flex-wrap justify-center gap-6">
            {services.map((service, index) => (
              <div key={index} className="w-full sm:w-[48%] md:w-[32%] lg:w-[23%]">
                <ServiceCard service={service} />
              </div>
            ))}
          </div>
        </motion.div>

      </motion.div>
    </section>
  );
};

export default OnlyServicesSection;